import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { TableColumn } from "../types";
import {
  SortAscIcon,
  SortDescIcon,
  DotsVerticalIcon,
  PencilIcon,
  ArchiveIcon,
  RestoreIcon,
} from "./Icons";

const MENU_WIDTH = 176; // w-44
const MENU_ITEM_HEIGHT = 36;


interface DataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  onRowSelect?: (row: T, selected: boolean) => void;
  selectedRows?: Set<string | number>;
  getRowId: (row: T) => string | number;
  onRowAction?: (row: T, action: string) => void;
  /** Custom renderer for the action cell; replaces the default dots-menu button */
  actionRenderer?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  /** Max height of the scrollable body. Default: calc(100vh - 320px) */
  maxHeight?: string;
  /** When true, context menu shows "Restore" instead of "Archive" */
  archiveMode?: boolean;
}

function DataTable<T>({
  columns,
  data,
  onRowSelect,
  selectedRows = new Set(),
  getRowId,
  onRowAction,
  actionRenderer,
  emptyMessage = "No data available",
  maxHeight = "calc(100vh - 320px)",
  archiveMode = false,
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [menuRowId, setMenuRowId] = useState<string | number | null>(null);
  // Portal anchor: position of the trigger button in the viewport
  const [menuAnchor, setMenuAnchor] = useState<{
    top: number;
    right: number;
    openUpward: boolean;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close context menu on outside click or scroll
  useEffect(() => {
    if (menuRowId === null) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuRowId(null);
        setMenuAnchor(null);
      }
    };
    const closeOnScroll = () => {
      setMenuRowId(null);
      setMenuAnchor(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [menuRowId]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const allSelected =
    data.length > 0 && data.every((row) => selectedRows.has(getRowId(row)));
  const someSelected =
    data.length > 0 && data.some((row) => selectedRows.has(getRowId(row)));

  // ── Sorted data ───────────────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aRaw = (a as any)[sortColumn];
      const bRaw = (b as any)[sortColumn];

      // Nulls always last
      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return 1;
      if (bRaw == null) return -1;

      const aVal = typeof aRaw === "string" ? aRaw.toLowerCase() : aRaw;
      const bVal = typeof bRaw === "string" ? bRaw.toLowerCase() : bRaw;

      let cmp = 0;
      if (aVal < bVal) cmp = -1;
      else if (aVal > bVal) cmp = 1;

      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [data, sortColumn, sortDirection]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    data.forEach((row) => onRowSelect && onRowSelect(row, checked));
  };

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  // Menu items count for height calculation
  const menuItemCount = archiveMode ? 1 : 2;
  const menuHeight = menuItemCount * MENU_ITEM_HEIGHT + 8; // py-1 padding

  const openMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    rowId: string | number
  ) => {
    e.stopPropagation();
    if (menuRowId === rowId) {
      setMenuRowId(null);
      setMenuAnchor(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setMenuRowId(rowId);
    setMenuAnchor({
      top: spaceBelow >= menuHeight + 8 ? rect.bottom + 4 : rect.top - menuHeight - 4,
      right: window.innerWidth - rect.right,
      openUpward: spaceBelow < menuHeight + 8,
    });
  };

  // ── Portal dropdown ───────────────────────────────────────────────────────
  const menuRow = sortedData.find((r) => getRowId(r) === menuRowId);
  const portalMenu =
    menuRowId !== null && menuAnchor && menuRow
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] w-44 bg-white border border-[#E5E7EB] rounded-lg shadow-xl overflow-hidden py-1"
            style={{ top: menuAnchor.top, right: menuAnchor.right }}
          >
            {!archiveMode && (
              <button
                onClick={() => {
                  onRowAction?.(menuRow, "edit");
                  setMenuRowId(null);
                  setMenuAnchor(null);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB] transition-colors"
              >
                <PencilIcon className="h-4 w-4 text-[#6B7280]" />
                Edit
              </button>
            )}
            {!archiveMode ? (
              <button
                onClick={() => {
                  onRowAction?.(menuRow, "archive");
                  setMenuRowId(null);
                  setMenuAnchor(null);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#374151] hover:bg-[#FEF2F2] hover:text-[#DC2626] transition-colors"
              >
                <ArchiveIcon className="h-4 w-4 text-[#6B7280]" />
                Archive
              </button>
            ) : (
              <button
                onClick={() => {
                  onRowAction?.(menuRow, "restore");
                  setMenuRowId(null);
                  setMenuAnchor(null);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#374151] hover:bg-[#F0FDF4] hover:text-[#15803D] transition-colors"
              >
                <RestoreIcon className="h-4 w-4 text-[#6B7280]" />
                Restore
              </button>
            )}
          </div>,
          document.body
        )
      : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="overflow-x-auto">
      {/* Scrollable body — only this area scrolls vertically */}
      <div style={{ maxHeight }} className="overflow-y-auto">
        <table className="min-w-full">
          {/* ── Sticky header ─────────────────────────────────────────── */}
          <thead className="bg-[#F9FAFB] sticky top-0 z-10 shadow-[0_1px_0_#E5E7EB]">
            <tr>
              {onRowSelect && (
                <th scope="col" className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-[#2563EB] border-[#D1D5DB] rounded cursor-pointer"
                  />
                </th>
              )}

              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={[
                    "px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider",
                    col.sortable
                      ? "cursor-pointer select-none hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors"
                      : "",
                  ].join(" ")}
                >
                  <div className="flex items-center space-x-1">
                    <span>{col.label}</span>
                    {col.sortable && (
                      <span className="flex flex-col leading-none ml-0.5">
                        <SortAscIcon
                          className={`h-3 w-3 ${
                            sortColumn === col.key && sortDirection === "asc"
                              ? "text-[#2563EB]"
                              : "text-[#D1D5DB]"
                          }`}
                        />
                        <SortDescIcon
                          className={`h-3 w-3 -mt-0.5 ${
                            sortColumn === col.key && sortDirection === "desc"
                              ? "text-[#2563EB]"
                              : "text-[#D1D5DB]"
                          }`}
                        />
                      </span>
                    )}
                  </div>
                </th>
              ))}

              {(onRowAction || actionRenderer) && (
                <th scope="col" className="px-4 py-3 w-12">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>

          {/* ── Body ──────────────────────────────────────────────────── */}
          <tbody className="bg-white divide-y divide-[#F3F4F6]">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (onRowSelect ? 1 : 0) +
                    (onRowAction || actionRenderer ? 1 : 0)
                  }
                  className="px-4 py-16 text-center text-sm text-[#9CA3AF]"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selectedRows.has(rowId);
                const isMenuOpen = menuRowId === rowId;

                return (
                  <tr
                    key={rowId}
                    className={`transition-colors ${
                      isSelected
                        ? "bg-[#EFF6FF] border-l-2 border-l-[#2563EB]"
                        : "hover:bg-[#F9FAFB] border-l-2 border-l-transparent"
                    }`}
                  >
                    {onRowSelect && (
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => onRowSelect(row, e.target.checked)}
                          className="h-4 w-4 text-[#2563EB] border-[#D1D5DB] rounded cursor-pointer"
                        />
                      </td>
                    )}

                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className="px-4 py-3.5 whitespace-nowrap text-sm text-[#111827]"
                      >
                        {col.render
                          ? col.render((row as any)[col.key], row)
                          : String((row as any)[col.key] ?? "—")}
                      </td>
                    ))}

                    {(onRowAction || actionRenderer) && (
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {actionRenderer ? (
                          actionRenderer(row)
                        ) : (
                          /* ── Trigger button only — dropdown rendered in portal ── */
                          <button
                            onClick={(e) => openMenu(e, rowId)}
                            className={`p-1 rounded transition-colors ${
                              isMenuOpen
                                ? "text-[#374151] bg-[#F3F4F6]"
                                : "text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6]"
                            }`}
                            aria-label="Row actions"
                          >
                            <DotsVerticalIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
    {portalMenu}
    </>
  );
}

export default DataTable;
